var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { ActiveWorkflowApi, ConsumptionApi, OrganizationManagementApi, OrganizationSubscriptionTypeSettingsApi, } from '@snapaddy/backend-client-axios';
import { Injectable } from 'angular-ts-decorators';
let BackendService = class BackendService {
    $http;
    accessTokenPromise;
    jwt;
    _consumptionApi;
    _orgaSubscriptionTypeSettingsApi;
    _activeWorkflowApi;
    _organizationManagementApi;
    // Minification-safe CI injections, everything @Inject()ed somewhere must be mentioned here:
    static $inject = ['$http'];
    constructor($http) {
        this.$http = $http;
    }
    get consumptionApi() {
        if (!this._consumptionApi) {
            this._consumptionApi = new ConsumptionApi(undefined, `${this.getBackendUrl()}/consumption`);
        }
        return this._consumptionApi;
    }
    get activeWorkflowApi() {
        if (!this._activeWorkflowApi) {
            this._activeWorkflowApi = new ActiveWorkflowApi(undefined, `${this.getBackendUrl()}/workflow`);
        }
        return this._activeWorkflowApi;
    }
    get organizationManagementApi() {
        if (!this._organizationManagementApi) {
            this._organizationManagementApi = new OrganizationManagementApi(undefined, `${this.getBackendUrl()}/organization`);
        }
        return this._organizationManagementApi;
    }
    get organizationSubscriptionTypeSettingsApi() {
        if (!this._orgaSubscriptionTypeSettingsApi) {
            this._orgaSubscriptionTypeSettingsApi = new OrganizationSubscriptionTypeSettingsApi(undefined, `${this.getBackendUrl()}/organization`);
        }
        return this._orgaSubscriptionTypeSettingsApi;
    }
    async switchOrganizationAsSnapaddyAdmin(organizationId) {
        await this.organizationManagementApi.switchOrganization({ organizationId }, { headers: { 'x-access-token': await this.getAccessToken() } });
    }
    /**
     * Gets the watchdog JWT used to authenticate backend-client calls, fetching it lazily on first
     * use and caching it afterwards. Callers must await this instead of reading a pre-populated token,
     * so there is no init race and no request is made until a call actually needs it.
     */
    async getAccessToken() {
        if (this.jwt)
            return this.jwt;
        if (!this.accessTokenPromise) {
            this.accessTokenPromise = this.$http
                .post('/watchdog/v1/user/me/access-token', {})
                .then(({ data }) => data.token);
        }
        try {
            this.jwt = await this.accessTokenPromise;
            return this.jwt;
        }
        catch (error) {
            this.accessTokenPromise = undefined;
            throw error;
        }
    }
    getBackendUrl() {
        const backendUrl = window.__snapADDY.backendUrl;
        if (!backendUrl)
            throw new Error('Backend URL is not configured');
        return backendUrl;
    }
    /**
     * Gets the consumption supply for all data enrichment supplies
     * @param organizationId The unique identifier for an organization
     * @returns An array of all data enrichment consumption supplies
     *
     * consumption supply object:
     * {
     *    "supplyId": "string",
     *    "organizationId": "string",
     *    "userId": "*",
     *    "resourceId": "string",
     *    "maxRequests": number,
     *    "resourceName": "string"
     * }
     */
    async getDataEnrichmentMaxSupply(organizationId) {
        const res = await this.consumptionApi.getDataEnrichmentSupplies(organizationId, {
            headers: {
                'x-access-token': await this.getAccessToken(),
            },
        });
        return res.data;
    }
    /**
     * Updates the maximum supply, or rather the max requests for a supply, for all data enrichment consumption supplies
     * @param organizationId The unique identifier for an organization
     * @param companyDataSearchMaxRequests The new max request value the company data search supply should be updated to
     * @param emailFinderMaxRequests The new max request value the email finder supply should be updated to
     * @returns The response object where `data` includes the updated consumption supply objects
     */
    async updateDataEnrichmentMaxSupply(organizationId, companyDataSearchMaxRequests, emailFinderMaxRequests) {
        try {
            return await this.consumptionApi.updateDataEnrichmentSupply(organizationId, {
                maxCompanyDataSearchRequests: companyDataSearchMaxRequests,
                maxEmailFinderRequests: emailFinderMaxRequests,
            }, {
                headers: {
                    'x-access-token': await this.getAccessToken(),
                },
            });
        }
        catch (error) {
            console.error('Failed to update DataEnrichment credits', error);
        }
    }
    /**
     * Creates a billwerk customer.
     *
     * @param organizationId unique identifier of the organization to create the billwerk customer for
     * @param organizationName name of the organization to create the billwerk customer for
     * @param legalEntityId id of the legal entity to create the billwerk customer in
     * @param legalEntityIdGmbh id of the snapADDY GmbH legal entity, used to derive the customer locale
     * @param user user object to get contact information from
     */
    async createBillwerkCustomerForOrganization(organizationId, organizationName, legalEntityId, legalEntityIdGmbh, user) {
        try {
            return await this.$http.post(new URL('/payment/v1/provider/customer/organization/' + organizationId, this.getBackendUrl()).href, {
                email: user?.username ?? '',
                firstName: user?.profile?.firstName ?? '',
                lastName: user?.profile?.lastName ?? '',
                organizationName,
                // We set DE even for snapaddy Inc. because the endpoint we use would require a state if country is US and we
                // don't take any details here yet. The snapaddy admin will have to set the full address manually anyway.
                country: 'DE',
                city: '',
                street: '',
                zip: '',
                locale: legalEntityId === legalEntityIdGmbh ? 'de' : 'en',
                // Setting defaultBearerMedium to archive only if not user was provided
                // otherwise billwerk will throw an error
                defaultBearerMedium: user ? 'Email' : 'ArchiveOnly',
                legalEntityId,
            }, {
                headers: {
                    'x-access-token': await this.getAccessToken(),
                },
            });
        }
        catch (error) {
            console.error('Cannot create billwerk customer for organization', error);
            throw new Error('CANNOT_CREATE_BILLWERK_CUSTOMER');
        }
    }
    async syncBillwerkSubscriptions(contractId, organizationId) {
        try {
            return await this.$http.post(new URL(`/payment/v1/provider/contract/${contractId}/sync-subscriptions`, this.getBackendUrl()).href, {
                organizationId,
            }, {
                headers: {
                    'x-access-token': await this.getAccessToken(),
                },
            });
        }
        catch (error) {
            console.error('Cannot sync billwerk subscriptions', error);
            throw new Error('CANNOT_SYNC_BILLWERK_SUBSCRIPTIONS');
        }
    }
    /**
     * Gets the feature flags of an organization using the new consumption/feature-flag endpoint
     * @param organization_id the unique identifier of the organization to retrieve the feature flags for
     * @returns Array with the names of all active flags
     */
    async getConsumptionFeatureFlags(organization_id) {
        let res;
        try {
            res = await this.$http.get(new URL(`/consumption/feature-flag/${organization_id}`, this.getBackendUrl()).href, {
                headers: {
                    'x-access-token': await this.getAccessToken(),
                },
            });
        }
        catch (error) {
            console.error('Failed to fetch feature flags', error);
            return [];
        }
        return res.data.featureFlags ?? [];
    }
    /**
     * Sets the feature flags of an organization using the new consumption/feature-flag endpoint
     * @param organization_id
     * @param newFeatureFlags - String array with the names of all active feature flags
     * @param renderCards - Re-render cards if true
     * @returns
     */
    async setConsumptionFeatureFlags(organization_id, newFeatureFlags, renderCards) {
        try {
            const accessToken = await this.getAccessToken();
            // Set feature flags
            await this.$http.put(new URL(`/consumption/feature-flag/${organization_id}`, this.getBackendUrl()).href, {
                featureFlags: newFeatureFlags,
            }, {
                headers: {
                    'x-access-token': accessToken,
                },
            });
            if (renderCards) {
                // Re-render digital cards (needed for the WHITE_LABEL_DIGITAL_CARD feature flag)
                await this.$http.post(new URL(`/digital-card/digital-card-management/render`, this.getBackendUrl()).href, {
                    organizationId: organization_id,
                }, {
                    headers: {
                        'x-access-token': accessToken,
                    },
                });
            }
        }
        catch (error) {
            console.error('Failed to set feature flags', error);
        }
    }
    async getDataAgentsCredits(organizationId) {
        const res = await this.consumptionApi.getDataAgentsAvailableCredits(organizationId, {
            headers: {
                'x-access-token': await this.getAccessToken(),
            },
        });
        return res.data;
    }
    async updateDataAgentsCredits(organizationId, newMaxCredits) {
        // Update the supply, this decides the max credits for new tracking windows.
        await this.consumptionApi.upsertDataAgentsMaxCredits(organizationId, { maxCredits: newMaxCredits }, {
            headers: {
                'x-access-token': await this.getAccessToken(),
            },
        });
    }
    async setSpentDataAgentsCredits(organizationId, amountToSetTo) {
        const updateRes = await this.consumptionApi.setSpentDataAgentsCredits(organizationId, { amountToSetTo }, {
            headers: {
                'x-access-token': await this.getAccessToken(),
            },
        });
        return updateRes.data;
    }
    async getActiveWorkflowLimit(organizationId) {
        const dataResult = await this.activeWorkflowApi.getActiveWorkflowNumberDataForOrganization(organizationId, {
            headers: {
                'x-access-token': this.jwt,
            },
        });
        return dataResult.data;
    }
    async setActiveWorkflowLimit(organizationSubscriptionTypeId, newValue) {
        await this.organizationSubscriptionTypeSettingsApi.setSettingByKey(organizationSubscriptionTypeId, { settingKey: 'DATA_AGENTS_MAX_CONCURRENT_WORKFLOWS', newValue }, {
            headers: {
                'x-access-token': this.jwt,
            },
        });
    }
    async removeActiveWorkflowLimit(organizationSubscriptionTypeId) {
        await this.organizationSubscriptionTypeSettingsApi.deleteSettingByKey(organizationSubscriptionTypeId, 'DATA_AGENTS_MAX_CONCURRENT_WORKFLOWS', {
            headers: {
                'x-access-token': this.jwt,
            },
        });
    }
};
BackendService = __decorate([
    Injectable('BackendService'),
    __metadata("design:paramtypes", [typeof (_a = typeof ng !== "undefined" && ng.IHttpService) === "function" ? _a : Object])
], BackendService);
export { BackendService };
